/**
 * ChapterPageView — renders a chapter-scoped custom page authored
 * through the admin PageEditor (spec-080).
 *
 * Route: /c/:chapterSlug/m/p/:pageSlug
 *
 * Resolves the chapter by slug, then loads the page via
 * pagesApi.getBySlug('chapter', chapter.id, pageSlug) and renders
 * its parts through PageRenderer.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  ApiClientError,
  chaptersApi,
  pagesApi,
  type ChapterSummary,
  type PageDetail,
} from '@/services/api';
import { PageRenderer } from '@/components/pages/PageRenderer';

export function ChapterPageView() {
  const params = useParams<{ chapterSlug: string; pageSlug: string }>();
  const [chapter, setChapter] = useState<ChapterSummary | null>(null);
  const [page, setPage] = useState<PageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    chaptersApi.get(params.chapterSlug!)
      .then((c) => {
        if (cancelled) return;
        setChapter(c);
        return pagesApi.getBySlug('chapter', c.id, params.pageSlug!);
      })
      .then((p) => { if (!cancelled && p) setPage(p); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiClientError) setError(err.message);
        else setError(err instanceof Error ? err.message : 'Failed to load page');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params.chapterSlug, params.pageSlug]);

  if (loading) return <div className="p-6 text-white/60">Loading…</div>;
  if (!page) {
    return (
      <div className="p-6">
        <p className="text-red-400 mb-2">{error ?? 'Page not found.'}</p>
        <Link to={`/c/${params.chapterSlug}/m/dashboard`} className="link text-sm">← Back to dashboard</Link>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <header className="mb-4">
        <h1 className="text-2xl font-display font-bold text-white">{page.title}</h1>
        {chapter && <p className="text-xs text-white/40">in {chapter.displayName}</p>}
      </header>
      <PageRenderer page={page} />
    </div>
  );
}

export default ChapterPageView;
